import type {
  Application,
  ApplicationMetrics,
  LogWithRelations,
  MessageTypeWithStats,
} from '@owl/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Range, ShellCtx } from '../components/Shell';
import { useToast } from '../components/Toast';
import { DonutChart } from '../components/charts/DonutChart';
import {
  StackedTimeline,
  type TimelineSelection,
  readHiddenTimelineTypes,
} from '../components/charts/StackedTimeline';
import { Icon } from '../components/icons';
import { AppHealthDot, EnvChip, Kpi, MessageTypeChip } from '../components/shared';
import { api } from '../lib/api';
import { bucketEndIso, formatBucketRange } from '../lib/buckets';
import { connectLogStream } from '../lib/sse';
import { formatDuration, formatTime, rangeIso, timeAgo } from '../lib/time';
import { useLoader } from '../lib/useLoader';

// A refresh must not erase what the live stream added. Rows at least as new as the
// newest fetched one — typically logs that landed between the request and its response —
// stay on top; everything else comes from the fresh page, which is authoritative.
function mergeRefreshed(fresh: LogWithRelations[], prev: LogWithRelations[]): LogWithRelations[] {
  const newestFetched = fresh[0] ? Date.parse(fresh[0].timestamp) : Number.NEGATIVE_INFINITY;
  const fetchedIds = new Set(fresh.map((l) => l.id));
  const streamedOnly = prev.filter(
    (l) => !fetchedIds.has(l.id) && Date.parse(l.timestamp) >= newestFetched,
  );
  return [...streamedOnly, ...fresh].slice(0, 500);
}

// How far back the list reaches when centred on a log. A page of 200 is the real
// bound; this only has to be generous enough to hold that page.
const FOCUS_LOOKBACK_MS = 24 * 60 * 60_000;

export function AppDashboard({
  ctx,
  applicationId,
  logId,
  prefill,
}: {
  ctx: ShellCtx;
  applicationId: string;
  logId?: string;
  prefill?: { messageTypeIds?: string[] };
}) {
  const [app, setApp] = useState<Application | null>(null);
  const [messageTypes, setMessageTypes] = useState<MessageTypeWithStats[]>([]);
  const [items, setItems] = useState<LogWithRelations[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [live, setLive] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(
    () => new Set(prefill?.messageTypeIds ?? []),
  );

  // Re-apply the prefill whenever the navigation target changes (e.g. user
  // clicks a different counter in the Overview "App health" card).
  // prefillKey is the joined id list: comparing the ids themselves is what decides
  // whether this is a new navigation target, since the array identity changes on
  // every render even when the selection has not.
  const prefillKey = prefill?.messageTypeIds?.join(',') ?? '';
  useEffect(() => {
    if (prefillKey) setSelectedTypes(new Set(prefillKey.split(',')));
  }, [prefillKey]);
  const [openDrawer, setOpenDrawer] = useState<LogWithRelations | null>(null);
  const [metrics, setMetrics] = useState<ApplicationMetrics | null>(null);
  // Bar clicked on the timeline: narrows the log list below to that time window.
  const [bucketSel, setBucketSel] = useState<TimelineSelection | null>(null);
  // Set when arriving on one specific log — an alert on the timeline or a notification
  // link. The list is paged back from that log so its row is guaranteed to be there: a
  // busy app writes more than a page of logs within the hour, so the newest page alone
  // would usually miss it. Live is paused meanwhile so the row does not slide away.
  const [focus, setFocus] = useState<{ logId: string; timestamp: string } | null>(null);
  // Mirrors the timeline legend so the distribution donut hides the same types.
  const [hiddenMtNames, setHiddenMtNames] = useState<Set<string>>(() => readHiddenTimelineTypes());
  const { push } = useToast();

  // rangeIso stamps `to` with the current time, so it has to be recomputed on every
  // refresh. Keyed on the range alone, the window froze at the moment the view mounted
  // and every refresh — button, interval, live toggle — re-queried that same closed
  // window and got the same rows back.
  const range = useMemo(() => {
    void ctx.refreshTick;
    return rangeIso(ctx.range);
  }, [ctx.range, ctx.refreshTick]);
  // The log list follows a focused log first, then a selected bucket, then the range.
  const logRange = useMemo(() => {
    if (focus) {
      // Ending at the focused log puts it at the top with what led up to it below. The
      // lower bound is a day back rather than the range start, so a notification link
      // to a log older than a short range (1h, 6h) still resolves.
      return {
        from: new Date(Date.parse(focus.timestamp) - FOCUS_LOOKBACK_MS).toISOString(),
        to: focus.timestamp,
      };
    }
    if (bucketSel) {
      return {
        from: new Date(bucketSel.fromMs).toISOString(),
        to: bucketEndIso(bucketSel.fromMs, bucketSel.size),
      };
    }
    return range;
  }, [focus, bucketSel, range]);

  // A range change, or moving to another application, invalidates a bucket picked
  // on the previous chart.
  const chartScope = `${ctx.range}|${applicationId}`;
  useEffect(() => {
    void chartScope;
    setBucketSel(null);
  }, [chartScope]);

  const focusedRowRef = useRef<HTMLButtonElement | null>(null);
  const scrolledToRef = useRef<string | null>(null);

  // Loaded state ref to avoid re-firing the initial query during dev StrictMode double mount
  const loadKeyRef = useRef('');
  // What the list is showing, independent of the window's moving edge. A reload within
  // the same scope merges into the current rows; a change of app, range, filter or
  // search replaces them.
  const listScope = `${applicationId}|${focus ? `focus:${focus.logId}` : bucketSel ? `${logRange.from}|${logRange.to}` : ctx.range}|${[...selectedTypes].join(',')}|${search}`;
  const listScopeRef = useRef('');

  const load = useCallback(async () => {
    const key = `${applicationId}|${logRange.from}|${logRange.to}|${[...selectedTypes].join(',')}|${search}`;
    loadKeyRef.current = key;
    setLoading(true);
    setError(null);
    try {
      const [{ items: logs, nextCursor: nc }, mtRes, appRes] = await Promise.all([
        api.listLogs({
          applicationId,
          messageTypeIds: selectedTypes.size > 0 ? [...selectedTypes] : undefined,
          search: search || undefined,
          from: logRange.from,
          to: logRange.to,
          limit: 200,
        }),
        api.listMessageTypes(),
        api.getApplication(applicationId),
      ]);
      if (loadKeyRef.current !== key) return;
      const sameScope = listScopeRef.current === listScope;
      listScopeRef.current = listScope;
      setItems((prev) => (sameScope ? mergeRefreshed(logs, prev) : logs));
      setNextCursor(nc);
      setMessageTypes(mtRes.items);
      setApp(appRes.application);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [applicationId, logRange.from, logRange.to, selectedTypes, search, listScope]);

  useLoader(load, ctx.refreshTick);

  const loadMetrics = useCallback(
    async (isCancelled: () => boolean) => {
      try {
        const m = await api.applicationMetrics(applicationId, range.from, range.to);
        if (!isCancelled()) setMetrics(m);
      } catch {
        if (!isCancelled()) setMetrics(null);
      }
    },
    [applicationId, range.from, range.to],
  );

  useLoader(loadMetrics, ctx.refreshTick);

  useEffect(() => {
    if (!live || bucketSel || focus) return;
    const disconnect = connectLogStream(
      {
        applicationId,
        messageTypeIds: selectedTypes.size > 0 ? [...selectedTypes] : undefined,
      },
      {
        onLog: (log) => {
          // A refresh can fetch a row before its stream event arrives; skip the repeat.
          setItems((prev) =>
            prev.some((p) => p.id === log.id) ? prev : [log, ...prev].slice(0, 500),
          );
        },
        onAlert: () => {
          push('Alert triggered');
        },
        onError: () => {
          // EventSource auto-reconnects on its own; just leave the connection going.
        },
      },
    );
    return disconnect;
  }, [live, bucketSel, focus, applicationId, selectedTypes, push]);

  useEffect(() => {
    if (!logId) {
      setFocus(null);
      return;
    }
    let cancelled = false;
    api
      .getLog(logId)
      .then((r) => {
        if (cancelled) return;
        setOpenDrawer(r.log);
        setFocus({ logId, timestamp: r.log.timestamp });
        setBucketSel(null);
        // A type filter could hide the very row being focused.
        setSelectedTypes(new Set());
      })
      .catch(() => {
        if (!cancelled) setError('Ese log ya no existe: probablemente lo purgó la retención.');
      });
    return () => {
      cancelled = true;
    };
  }, [logId]);

  useEffect(() => {
    // The row exists only once its page has rendered, so re-check as the list changes.
    void items;
    if (!focus || scrolledToRef.current === focus.logId) return;
    const row = focusedRowRef.current;
    if (!row) return;
    // Once per target, or every refresh would drag the view back to the row.
    scrolledToRef.current = focus.logId;
    row.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [focus, items]);

  function toggleType(id: string) {
    setSelectedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const distributionData = useMemo(() => {
    if (!metrics) return [];
    const byId = new Map(messageTypes.map((m) => [m.id, m]));
    return metrics.distribution
      .map((d) => {
        const mt = byId.get(d.messageTypeId);
        if (!mt || hiddenMtNames.has(mt.name)) return null;
        return { value: d.count, color: mt.color, label: mt.name };
      })
      .filter((d): d is { value: number; color: string; label: string } => d !== null)
      .sort((a, b) => b.value - a.value);
  }, [metrics, messageTypes, hiddenMtNames]);

  const distributionTotal = distributionData.reduce((sum, d) => sum + d.value, 0);

  const errorRateDelta = useMemo(() => {
    if (!metrics) return '—';
    const prev = metrics.totals.previousErrorRate;
    if (prev === null) return `rango ${ctx.range === 'mtd' ? 'MTD' : ctx.range}`;
    const diffPp = (metrics.totals.errorRate - prev) * 100;
    if (Math.abs(diffPp) < 0.005) return 'sin cambio vs periodo prev';
    return `${diffPp > 0 ? '↑' : '↓'} ${Math.abs(diffPp).toFixed(2)}pp vs periodo prev`;
  }, [metrics, ctx.range]);

  async function loadMore() {
    if (!nextCursor) return;
    setLoading(true);
    try {
      const { items: more, nextCursor: nc } = await api.listLogs({
        applicationId,
        messageTypeIds: selectedTypes.size > 0 ? [...selectedTypes] : undefined,
        search: search || undefined,
        from: logRange.from,
        to: logRange.to,
        cursor: nextCursor,
        limit: 200,
      });
      setItems((prev) => [...prev, ...more]);
      setNextCursor(nc);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="view-pad">
      <div className="view-head">
        <div>
          <div className="view-sub">
            <button
              type="button"
              className="link-btn"
              onClick={() => ctx.navigate({ name: 'applications' })}
            >
              <Icon.ChevronLeft size={11} /> Applications
            </button>
          </div>
          <div className="view-title" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {app?.name ?? '…'}
            {app && <EnvChip environment={app.environment} />}
          </div>
          {app && (
            <div className="view-sub">
              <AppHealthDot app={app} /> {app.status} · token {app.tokenPrefix}… · last log{' '}
              {timeAgo(app.lastLogAt)}
              {metrics && metrics.lifetime.totalLogs > 0 && (
                <>
                  {' · '}
                  <span className="mono">
                    {metrics.lifetime.totalLogs.toLocaleString()} logs totales
                  </span>
                </>
              )}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div className="range-picker">
            {(['1h', '6h', '24h', '7d', '30d', 'mtd'] as const satisfies readonly Range[]).map(
              (r) => (
                <button
                  type="button"
                  key={r}
                  className={`range-opt ${ctx.range === r ? 'active' : ''}`}
                  onClick={() => ctx.setRange(r)}
                >
                  {r === 'mtd' ? 'MTD' : r}
                </button>
              ),
            )}
          </div>
          <button
            type="button"
            className={live ? 'live-toggle on' : 'live-toggle'}
            onClick={() => setLive((l) => !l)}
          >
            <span className="live-dot" /> {live ? 'Live' : 'Paused'}
          </button>
        </div>
      </div>

      {metrics && (
        <>
          <div className="kpi-row kpi-row-5">
            <Kpi
              label={`Total logs · ${ctx.range === 'mtd' ? 'MTD' : ctx.range}`}
              value={metrics.totals.logs.toLocaleString()}
              icon={Icon.Activity}
              delta={
                metrics.lifetime.totalLogs > 0
                  ? `${metrics.lifetime.totalLogs.toLocaleString()} lifetime`
                  : '—'
              }
            />
            <Kpi
              label="Error rate"
              value={(metrics.totals.errorRate * 100).toFixed(2)}
              unit="%"
              icon={Icon.Alert}
              delta={errorRateDelta}
            />
            <Kpi
              label="Uptime"
              value={
                metrics.totals.uptime === null ? '—' : (metrics.totals.uptime * 100).toFixed(2)
              }
              unit={metrics.totals.uptime === null ? undefined : '%'}
              icon={Icon.Server}
              delta={
                metrics.totals.uptime === null
                  ? 'sin datos'
                  : metrics.totals.downSeconds > 0
                    ? `${formatDuration(metrics.totals.downSeconds)} sin señal`
                    : 'sin caídas'
              }
            />
            <Kpi
              label="Alerts triggered"
              value={metrics.totals.alertsTriggered}
              icon={Icon.Alerts}
              delta={
                metrics.recentAlerts.length > 0 ? `${metrics.recentAlerts.length} recientes` : '—'
              }
            />
            <Kpi
              label="Latencia media"
              value={metrics.avgLatency !== null ? metrics.avgLatency.toFixed(0) : '—'}
              unit={metrics.avgLatency !== null ? 'ms' : undefined}
              icon={Icon.Bolt}
              delta={metrics.p95Latency !== null ? `p95 ${metrics.p95Latency.toFixed(0)}ms` : '—'}
            />
          </div>

          <div className="grid-appdash-charts">
            <div className="card">
              <div className="card-h">
                <div>
                  <div className="card-t">
                    Volumen de logs · {ctx.range === 'mtd' ? 'mes en curso' : `rango ${ctx.range}`}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--fg-4)', marginTop: 4 }}>
                    Pulsa una barra para filtrar los logs de ese intervalo
                  </div>
                </div>
              </div>
              <div className="card-body">
                {metrics.timeline.length === 0 ? (
                  <div className="feed-empty">No data in this range</div>
                ) : (
                  <StackedTimeline
                    timeline={metrics.timeline}
                    messageTypes={messageTypes}
                    alertEvents={metrics.recentAlerts}
                    from={range.from}
                    to={range.to}
                    height={180}
                    onHiddenTypesChange={setHiddenMtNames}
                    onAlertSelect={(alert) => {
                      if (!alert.triggeringLogId) return;
                      ctx.navigate({
                        name: 'appdash',
                        applicationId: alert.applicationId,
                        logId: alert.triggeringLogId,
                      });
                    }}
                    onBucketSelect={(sel) => {
                      // Picking a bar is a different question from "show me this log".
                      if (focus) ctx.navigate({ name: 'appdash', applicationId });
                      setBucketSel(sel);
                      // Clicking one stacked segment also narrows the type filter.
                      if (sel.messageTypeId) setSelectedTypes(new Set([sel.messageTypeId]));
                    }}
                  />
                )}
              </div>
            </div>

            <div className="card">
              <div className="card-h">
                <div className="card-t">
                  <span className="serif-i" style={{ fontSize: 15 }}>
                    Distribución
                  </span>
                </div>
              </div>
              <div className="card-body appdash-donut">
                {distributionData.length === 0 ? (
                  <div className="feed-empty">No logs in this range</div>
                ) : (
                  <>
                    <DonutChart
                      data={distributionData}
                      size={124}
                      thickness={16}
                      centerLabel={`LOGS · ${
                        ctx.range === 'mtd' ? 'MTD' : ctx.range.toUpperCase()
                      }`}
                      centerValue={distributionTotal.toLocaleString()}
                      onSegmentClick={(i) => {
                        const d = distributionData[i];
                        const mt = messageTypes.find((m) => m.name === d?.label);
                        if (mt) setSelectedTypes(new Set([mt.id]));
                      }}
                    />
                    <div className="appdash-donut-legend">
                      {distributionData.map((d) => (
                        <div key={d.label} className="dist-legend-row">
                          <span
                            style={{ width: 8, height: 8, borderRadius: 2, background: d.color }}
                          />
                          <span className="mono" style={{ fontSize: 10.5 }}>
                            {d.label}
                          </span>
                          <span
                            className="mono"
                            style={{ marginLeft: 'auto', color: 'var(--fg-3)' }}
                          >
                            {d.value.toLocaleString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      <div className="panel">
        <div className="filter-bar">
          {bucketSel && (
            <button
              type="button"
              className="filter-chip active"
              onClick={() => setBucketSel(null)}
              title="Quitar el filtro de intervalo"
            >
              {formatBucketRange(bucketSel.fromMs, bucketSel.size)} <Icon.X size={10} />
            </button>
          )}
          <input
            placeholder="Search messages…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {messageTypes.map((mt) => (
            <button
              type="button"
              key={mt.id}
              onClick={() => toggleType(mt.id)}
              className={`mt-toggle ${selectedTypes.has(mt.id) ? 'active' : ''}`}
              style={
                selectedTypes.has(mt.id)
                  ? { background: `${mt.color}1F`, borderColor: `${mt.color}55`, color: mt.color }
                  : undefined
              }
            >
              {mt.name}
            </button>
          ))}
          {selectedTypes.size > 0 && (
            <button
              type="button"
              className="icon-btn"
              onClick={() => setSelectedTypes(new Set())}
              title="Clear"
            >
              <Icon.X size={12} />
            </button>
          )}
          <button type="button" className="icon-btn" onClick={ctx.triggerRefresh} title="Refresh">
            <Icon.Refresh size={12} />
          </button>
        </div>

        {focus && (
          <div className="feed-focus-banner">
            <span>Centrado en el log de la alerta · directo pausado · sin filtro de tipo</span>
            <button
              type="button"
              className="cta-secondary"
              onClick={() => ctx.navigate({ name: 'appdash', applicationId })}
            >
              Ver recientes
            </button>
          </div>
        )}
        {error && (
          <div className="error-banner" style={{ margin: 12 }}>
            {error}
          </div>
        )}

        {loading && items.length === 0 ? (
          <div className="feed-empty">Loading…</div>
        ) : items.length === 0 ? (
          <div className="feed-empty">No logs in this range</div>
        ) : (
          <div className="table-wrap">
            {items.map((log) => (
              <button
                type="button"
                className={log.id === focus?.logId ? 'feed-row focused' : 'feed-row'}
                key={log.id}
                ref={log.id === focus?.logId ? focusedRowRef : undefined}
                onClick={() => setOpenDrawer(log)}
              >
                <div className="feed-time">{formatTime(log.timestamp)}</div>
                <div>
                  <MessageTypeChip mt={log.messageType} dense />
                </div>
                <div className="feed-msg">{log.message}</div>
              </button>
            ))}
            {nextCursor && (
              <div className="feed-empty">
                <button
                  type="button"
                  className="cta-secondary"
                  onClick={loadMore}
                  disabled={loading}
                >
                  {loading ? 'Loading…' : 'Load more'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {openDrawer && <LogDrawer log={openDrawer} ctx={ctx} onClose={() => setOpenDrawer(null)} />}
    </div>
  );
}

function LogDrawer({
  log,
  ctx,
  onClose,
}: {
  log: LogWithRelations;
  ctx: ShellCtx;
  onClose: () => void;
}) {
  return (
    <>
      <button
        type="button"
        className="drawer-backdrop"
        aria-label="Close log detail"
        onClick={onClose}
      />
      <aside className="drawer">
        <div className="drawer-head">
          <div>
            <MessageTypeChip mt={log.messageType} />
            <span className="dim mono" style={{ marginLeft: 8 }}>
              {formatTime(log.timestamp)}
            </span>
          </div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <Icon.X size={14} />
          </button>
        </div>
        <div className="drawer-body">
          <div>
            <div className="section-title">Message</div>
            <div className="json-block">{log.message}</div>
          </div>
          <div>
            <div className="section-title">Metadata</div>
            <pre className="json-block">{JSON.stringify(log.metadata, null, 2)}</pre>
          </div>
          <div>
            <div className="section-title">Context</div>
            <div className="dim">
              <strong>{log.application.name}</strong> · {log.application.product} /{' '}
              {log.application.company}
              {log.application.instance ? ` / ${log.application.instance}` : ''}
            </div>
            <div className="dim mono" style={{ marginTop: 4 }}>
              {log.id}
            </div>
          </div>
          <button
            type="button"
            className="cta-secondary"
            onClick={() =>
              ctx.navigate({
                name: 'alerts',
                prefill: {
                  applicationId: log.applicationId,
                  messageTypeId: log.messageTypeId,
                  pattern: log.message,
                },
              })
            }
          >
            <Icon.Plus size={12} /> Create alert from this log
          </button>
        </div>
      </aside>
    </>
  );
}
